import React, { useEffect, useState, useMemo } from 'react';
import { apiClient } from '../utils/apiClient';
import { saveAs } from 'file-saver';
import { useAuth } from '../contexts/AuthContext';
import { formatDateForApi } from '../utils/dateUtils';
import {
    Loader2,
    AlertCircle,
    Info,
    Filter as FilterIcon,
    Calendar,
    Search,
    X,
    FileSpreadsheet,
    FileText,
    Table as TableIcon
} from 'lucide-react';

const ReportRenderer = ({
    request,
    reportType: directReportType,
    filters: directFilters,
    hideDateBadge = false
}) => {
    const { permissions = [] } = useAuth();
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState({ pdf: false, excel: false });
    const [error, setError] = useState(null);
    const [quickFilter, setQuickFilter] = useState('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const ROWS_PER_PAGE = 100;

    // Hem request={{ reportType, filters, key }} hem de doğrudan reportType/filters prop'larını destekle
    const effectiveRequest = useMemo(() => {
        if (request && request.reportType) {
            return request;
        }
        if (directReportType && directFilters) {
            return {
                reportType: directReportType,
                filters: directFilters
            };
        }
        return null;
    }, [request, directReportType, directFilters]);

    useEffect(() => {
        if (!effectiveRequest || !effectiveRequest.reportType) {
            setReportData(null);
            setError(null);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            setQuickFilter('');
            try {
                const rawFilters = effectiveRequest.filters || {};
                const response = await apiClient('Report/generate-report', {
                    method: 'POST',
                    body: JSON.stringify({
                        reportType: effectiveRequest.reportType,
                        filters: {
                            ...rawFilters,
                            startDate: formatDateForApi(rawFilters.startDate || new Date()),
                            endDate: formatDateForApi(rawFilters.endDate || new Date()),
                        }
                    }),
                });

                if (response && response.headers && response.rows) {
                    setReportData(response);
                    setCurrentPage(1);
                } else {
                    setReportData(null);
                    setError('Rapor verisi beklenmeyen bir formatta geldi.');
                }
            } catch (err) {
                setReportData(null);
                setError(err.message || 'Rapor verileri yüklenirken bir hata oluştu.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [effectiveRequest]);

    const exportReport = async (format) => {
        if (!effectiveRequest) return;
        setExportLoading(prev => ({ ...prev, [format]: true }));
        setError(null);

        try {
            const rawFilters = effectiveRequest.filters || {};
            const requestBody = {
                reportType: effectiveRequest.reportType,
                filters: {
                    ...rawFilters,
                    startDate: formatDateForApi(rawFilters.startDate || new Date()),
                    endDate: formatDateForApi(rawFilters.endDate || new Date()),
                }
            };

            const { blob, filename } = await apiClient(`Report/generate-${format}`, {
                method: 'POST',
                body: JSON.stringify(requestBody),
                responseType: 'blob',
            });
            saveAs(blob, filename);
        } catch (err) {
            setError(err.message || `${format.toUpperCase()} raporu indirilirken bir hata oluştu.`);
        } finally {
            setExportLoading(prev => ({ ...prev, [format]: false }));
        }
    };

    const formatCellValue = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        if (typeof value === 'string' && value.includes('T') && !isNaN(new Date(value).getTime())) {
            return new Date(value).toLocaleString('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        return value.toString();
    };

    const colorizeMatrix = effectiveRequest?.filters?.colorizeMatrix !== false;

    const renderStyledCell = (rawValue, header, isSummaryRow = false, colIndex = -1, isSubTotal = false, isGrandTotal = false) => {
        const val = formatCellValue(rawValue);
        const h = (header || '').toLocaleLowerCase('tr-TR');

        if (isSummaryRow && colIndex === 0) {
            return (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-extrabold uppercase tracking-wider shadow-sm ${
                    isGrandTotal
                        ? 'bg-emerald-700 text-white dark:bg-emerald-600'
                        : 'bg-amber-600 text-white dark:bg-amber-600'
                }`}>
                    <span>∑</span>
                    <span>{isGrandTotal ? 'GENEL TOPLAM' : 'TOPLAM'}</span>
                </span>
            );
        }

        // Yatay Puantaj Çizelgesi (monthly_attendance_matrix) gün hücreleri ("01".."31" veya "01.09")
        if (/^\d{2}(\.\d{2})?$/.test(h) && !isSummaryRow) {
            if (val === '-') return <span className="text-gray-300 dark:text-gray-600">-</span>;
            if (!colorizeMatrix) {
                return <span className="font-semibold text-gray-800 dark:text-gray-200">{val}</span>;
            }
            if (val === 'X') {
                return <span className="font-semibold text-gray-700 dark:text-gray-300">X</span>;
            }
            let matrixCls = 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-700';
            if (val === 'X+FM' || val === 'HT+' || val === 'RT+') {
                matrixCls = 'bg-emerald-100 text-emerald-800 border-emerald-400 dark:bg-emerald-900/70 dark:text-emerald-200 dark:border-emerald-600';
            } else if (val === 'X(G)') {
                matrixCls = 'bg-amber-100 text-amber-800 border-amber-400 dark:bg-amber-900/70 dark:text-amber-200 dark:border-amber-600';
            } else if (val === 'D') {
                matrixCls = 'bg-rose-200 text-rose-900 border-rose-400 dark:bg-rose-900/80 dark:text-rose-100 dark:border-rose-600';
            } else if (val === 'RP') {
                matrixCls = 'bg-orange-100 text-orange-800 border-orange-400 dark:bg-orange-900/70 dark:text-orange-200 dark:border-orange-600';
            } else if (val === 'HT' || val === 'RT') {
                matrixCls = 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
            } else {
                // Yİ, İZ, Üİ vb. izin kodları
                matrixCls = 'bg-purple-100 text-purple-800 border-purple-400 dark:bg-purple-900/70 dark:text-purple-200 dark:border-purple-600';
            }
            return (
                <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[11px] font-bold border min-w-[28px] shadow-2xs ${matrixCls}`}>
                    {val}
                </span>
            );
        }

        if (h.includes('durum') || h === 'geçiş tipi') {
            const vLower = val.toLocaleLowerCase('tr-TR');
            let badgeClass = 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-700';

            if (vLower.includes('tam gün') || vLower === 'normal' || vLower.includes('tamamlandı') || vLower === 'onayladı' || vLower === 'onaylandı' || vLower === 'aktif' || vLower === 'giriş') {
                badgeClass = 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-700';
            } else if (vLower.includes('fm') || vLower.includes('fazla mesai') || vLower.includes('çalıştı') || vLower.includes('toplam') || vLower.includes('icmal')) {
                badgeClass = 'bg-teal-50 text-teal-700 border-teal-300 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-700';
            } else if (vLower.includes('geç') || vLower.includes('erken') || vLower.includes('eksik') || vLower.includes('bekliyor')) {
                badgeClass = 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700';
            } else if (vLower.includes('devamsız') || vLower.includes('ayrıldı') || vLower === 'pasif' || vLower === 'çıkış') {
                badgeClass = 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-700';
            } else if (vLower.includes('izin') || vLower.includes('tatil')) {
                badgeClass = 'bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-700';
            }

            return (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badgeClass}`}>
                    {val}
                </span>
            );
        }

        return val;
    };

    const filteredRows = useMemo(() => {
        if (!reportData || !reportData.rows) return [];
        const q = quickFilter.trim().toLocaleLowerCase('tr-TR');
        if (!q) return reportData.rows;
        return reportData.rows.filter(row =>
            row.some(cell => cell && cell.toString().toLocaleLowerCase('tr-TR').includes(q))
        );
    }, [reportData, quickFilter]);

    if (!effectiveRequest) return null;

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mb-4" />
                <p className="text-gray-700 dark:text-gray-200 font-semibold">Rapor verileri hazırlanıyor...</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Seçilen kriterlere göre kayıtlar analiz ediliyor.</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-12 p-6 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-red-300 dark:border-red-800">
                <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center mb-4">
                    <AlertCircle className="w-7 h-7 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-1">Rapor Oluşturulamadı</h3>
                <p className="text-sm text-red-600 dark:text-red-400 text-center max-w-md">{error}</p>
            </div>
        );
    }

    if (!reportData || reportData.rows.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 p-6 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-4">
                    <Info className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-1">Kriterlere Uygun Kayıt Bulunamadı</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">
                    Seçtiğiniz filtre kriterlerine uygun veri bulunmamaktadır. Filtreleri genişleterek tekrar deneyebilirsiniz.
                </p>
            </div>
        );
    }

    const canExportExcel = permissions.includes('reports:export_excel');
    const canExportPdf = permissions.includes('reports:export_pdf');

    const activeFilterCount = Object.entries(effectiveRequest.filters || {}).filter(([k, v]) => {
        if (k === 'startDate' || k === 'endDate') return false;
        if (Array.isArray(v)) return v.length > 0;
        return !!v;
    }).length;

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);
    const currentRows = filteredRows.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

    return (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Üst Başlık, Arama & Dışa Aktarım Barı */}
            <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-gray-50 dark:bg-gray-800">
                <div>
                    <div className="flex flex-wrap items-center gap-2.5 mb-2">
                        <div className="inline-flex items-center text-xs font-bold tracking-wider px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded-lg border border-emerald-300 dark:border-emerald-700">
                            <TableIcon className="w-3.5 h-3.5 mr-1.5" />
                            RAPOR SONUÇ TABLOSU
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                            {reportData.title}
                        </h3>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        {!hideDateBadge && effectiveRequest.filters?.startDate && effectiveRequest.filters?.endDate && (
                            <div className="flex items-center bg-white dark:bg-gray-900 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700">
                                <Calendar className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />
                                <span className="font-medium">
                                    {new Date(effectiveRequest.filters.startDate).toLocaleDateString('tr-TR')} — {new Date(effectiveRequest.filters.endDate).toLocaleDateString('tr-TR')}
                                </span>
                            </div>
                        )}
                        {activeFilterCount > 0 && (
                            <div className="flex items-center bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-2.5 py-1 rounded-lg border border-amber-300 dark:border-amber-700">
                                <FilterIcon className="w-3.5 h-3.5 mr-1.5" />
                                <span className="font-semibold">{activeFilterCount} özel filtre uygulandı</span>
                            </div>
                        )}
                        <div className="flex items-center bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 rounded-lg border border-emerald-300 dark:border-emerald-700">
                            <span className="font-bold mr-1">{filteredRows.length}</span>
                            <span>kayıt listeleniyor</span>
                            {quickFilter && filteredRows.length !== reportData.rows.length && (
                                <span className="ml-1 text-xs opacity-75">(toplam {reportData.rows.length})</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2.5 w-full lg:w-auto">
                    {/* Tablo İçi Hızlı Arama */}
                    <div className="relative flex-1 sm:w-64">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                            type="text"
                            value={quickFilter}
                            onChange={(e) => {
                                setQuickFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Tablo içinde hızlı ara..."
                            className="w-full pl-9 pr-8 py-2 text-xs sm:text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none"
                        />
                        {quickFilter && (
                            <button
                                onClick={() => {
                                    setQuickFilter('');
                                    setCurrentPage(1);
                                }}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {canExportExcel && (
                        <button
                            onClick={() => exportReport('excel')}
                            disabled={exportLoading.excel}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs sm:text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm disabled:opacity-50 transition-all"
                        >
                            {exportLoading.excel ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                            <span>Excel (.xlsx)</span>
                        </button>
                    )}

                    {canExportPdf && (
                        <button
                            onClick={() => exportReport('pdf')}
                            disabled={exportLoading.pdf}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs sm:text-sm font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-lg shadow-sm disabled:opacity-50 transition-all"
                        >
                            {exportLoading.pdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                            <span>PDF İndir</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Tablo Alanı */}
            <div className="overflow-x-auto relative max-h-[680px] p-4 bg-white dark:bg-gray-900">
                <table className="min-w-full border-collapse text-xs sm:text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 sticky top-0 z-10">
                        <tr>
                            <th className="px-3 py-3 text-center font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12 border-b border-gray-200 dark:border-gray-700">
                                #
                            </th>
                            {reportData.headers.map((header, index) => (
                                <th
                                    key={index}
                                    className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap border-b border-gray-200 dark:border-gray-700"
                                >
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                        {currentRows.map((row, i) => {
                            const rowNum = (safePage - 1) * ROWS_PER_PAGE + i + 1;
                            const firstCell = String(row?.[0] || '').trim().toUpperCase();
                            const isSubTotal = firstCell === 'TOPLAM';
                            const isGrandTotal = firstCell === 'GENEL TOPLAM';
                            const isSummaryRow = isSubTotal || isGrandTotal;

                            let trClass = 'hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors';
                            if (isGrandTotal) {
                                trClass = 'bg-emerald-200/85 dark:bg-emerald-900/70 border-y-2 border-emerald-600 dark:border-emerald-500 font-extrabold shadow-xs';
                            } else if (isSubTotal) {
                                trClass = 'bg-amber-200/80 dark:bg-amber-900/60 border-y-2 border-amber-500 dark:border-amber-600 font-extrabold shadow-xs';
                            }

                            return (
                                <tr
                                    key={i}
                                    className={trClass}
                                >
                                    <td className={`px-3 py-2.5 text-center text-xs font-mono ${
                                        isGrandTotal
                                            ? 'bg-emerald-200/85 dark:bg-emerald-900/70 text-emerald-950 dark:text-emerald-100 font-extrabold'
                                            : isSubTotal
                                            ? 'bg-amber-200/80 dark:bg-amber-900/60 text-amber-950 dark:text-amber-100 font-extrabold'
                                            : 'text-gray-400 dark:text-gray-500'
                                    }`}>
                                        {isSummaryRow ? '∑' : rowNum}
                                    </td>
                                    {row.map((cellValue, j) => (
                                        <td
                                            key={j}
                                            className={`px-4 py-2.5 whitespace-nowrap ${
                                                isGrandTotal
                                                    ? 'bg-emerald-200/85 dark:bg-emerald-900/70 text-emerald-950 dark:text-emerald-100 font-extrabold'
                                                    : isSubTotal
                                                    ? 'bg-amber-200/80 dark:bg-amber-900/60 text-amber-950 dark:text-amber-100 font-extrabold'
                                                    : 'text-gray-700 dark:text-gray-200'
                                            }`}
                                        >
                                            {renderStyledCell(cellValue, reportData.headers[j], isSummaryRow, j, isSubTotal, isGrandTotal)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Sayfalama (Pagination) */}
            {totalPages > 1 && (
                <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                        Toplam <strong>{filteredRows.length}</strong> kayıttan{' '}
                        <strong>{(safePage - 1) * ROWS_PER_PAGE + 1}</strong> -{' '}
                        <strong>{Math.min(safePage * ROWS_PER_PAGE, filteredRows.length)}</strong> arası gösteriliyor
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={safePage === 1}
                            className="px-3.5 py-1.5 text-xs font-bold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Önceki
                        </button>
                        <div className="px-3.5 py-1.5 text-xs font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded-lg border border-emerald-300 dark:border-emerald-700">
                            Sayfa {safePage} / {totalPages}
                        </div>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={safePage === totalPages}
                            className="px-3.5 py-1.5 text-xs font-bold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Sonraki
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportRenderer;
